import { z } from 'zod';
import { attachment, dateStr, hexColor, objectIdStr } from './common.js';
import { stationKey } from './station.js';

export const JOB_STATUSES = ['draft', 'active', 'done', 'cancelled'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
export const jobStatus = z.enum(JOB_STATUSES);

/**
 * A dated note on a job. `summary` is the short form the job page shows before the
 * notes are expanded — derived from `text` on write, stored separately so it can be
 * reworded without touching the note itself (see `summariseNote`).
 */
export const jobNote = z.object({
  _id: objectIdStr,
  text: z.string().min(1),
  summary: z.string().min(1),
  createdAt: z.coerce.date(),
  createdBy: z.string().optional(),
  /** Set only once the note has been edited, so the UI can say so. */
  updatedAt: z.coerce.date().optional(),
});
export type JobNote = z.infer<typeof jobNote>;

export const job = z.object({
  _id: objectIdStr,
  name: z.string().min(1),
  customer: z.string().optional(),
  /** Higher wins when `replanAll` orders competing batches. */
  priority: z.number().int().default(1000),
  status: jobStatus,
  createdBy: z.string().optional(),
  createdAt: z.coerce.date(),
  /** Newest first. Absent on jobs created before notes existed, hence the default. */
  notes: z.array(jobNote).default([]),
  /** Reference material that belongs to the job as a whole rather than to one List
   * item — drawings, POs, anything that doesn't fit a row. Shown at the bottom of the
   * List page. */
  attachments: z.array(attachment).default([]),
  /**
   * How this job is highlighted on the board. Always present on the way out — a job
   * with none stored falls back to a stable colour derived from its id.
   */
  color: hexColor,
  /**
   * Set when the job has been put away. Archiving is a flag beside `status`, not a
   * fifth status: a job in the archive is still done or cancelled (or, rarely, active),
   * and restoring it must bring that back untouched. Archived jobs stay fully readable
   * by id; they are only left out of the default Jobs list and of "Re-plan all".
   */
  archivedAt: z.coerce.date().optional(),
  archivedBy: z.string().optional(),
});
export type Job = z.infer<typeof job>;

/** Only the full text is asked for; the summary is derived unless one is supplied. */
export const jobNoteCreate = z.object({
  text: z.string().trim().min(1),
  summary: z.string().trim().min(1).optional(),
});
export type JobNoteCreate = z.infer<typeof jobNoteCreate>;

/**
 * Clearing `summary` (sending `null`) re-derives it from the current text — which is
 * what a planner who has just rewritten the note usually wants.
 */
export const jobNotePatch = z.object({
  text: z.string().trim().min(1).optional(),
  summary: z.string().trim().min(1).nullable().optional(),
});
export type JobNotePatch = z.infer<typeof jobNotePatch>;

export const STEP_STATUSES = ['pending', 'inProgress', 'done'] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];
export const stepStatus = z.enum(STEP_STATUSES);

/**
 * Progress is authoritative and manual (SPEC §4) — a planner maintains it on the job
 * page. Scans never write here.
 */
export const stepActual = z.object({
  status: stepStatus.default('pending'),
  startedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
  by: z.string().optional(),
});
export type StepActual = z.infer<typeof stepActual>;

/**
 * How much of one job a station can absorb in a single day — the single knob for "this
 * job cannot take the station's whole day".
 *
 * Two quite different reasons produce it, and both are just a number of hours:
 * finishing waits on drying, so a booth working eight hours cannot put eight hours of
 * *this* job through; and installation is staffed per job, so one installer on site is
 * 8h a day and two are 16, however many the shop employs. Capping the step spreads the
 * work over the days it really takes and leaves the rest of the station's day for
 * other jobs.
 */
export const DEFAULT_MAX_HOURS_PER_DAY = 8;

/**
 * A hand-drag on the board. It is the same lower bound as `notBeforeDate`, kept in its
 * own field for two reasons: "Clear hand-moves" can undo a planner's dragging without
 * touching a date somebody deliberately fixed, and the UI can show "moved by hand"
 * distinctly from a real constraint. The engine never sees both — the service merges
 * them with `maxDate`, so a typed constraint always wins over a drag.
 */
export const planNudge = z.object({
  date: dateStr,
  by: z.string().optional(),
  at: z.coerce.date(),
});
export type PlanNudge = z.infer<typeof planNudge>;

export const routingStep = z.object({
  stationKey,
  hoursWork: z.number().min(0),
  bufferDays: z.number().int().min(0).default(0),
  notBeforeDate: dateStr.optional(),
  maxHoursPerDay: z.number().positive().default(DEFAULT_MAX_HOURS_PER_DAY),
  /**
   * Stations that must finish (+ this step's `bufferDays`) before this one may start.
   * Omitted means "the previous step in this routing" — see `defaultDependsOn` — so
   * plain sequential routings need never set it. Explicit only where the flow branches:
   * two stations run off one predecessor, or one station waits on more than one.
   */
  dependsOn: z.array(stationKey).optional(),
  /** Set by dragging this step's chip across the board — see `planNudge`. */
  nudge: planNudge.optional(),
  actual: stepActual.default({ status: 'pending' }),
});
export type RoutingStep = z.infer<typeof routingStep>;

export const batch = z.object({
  _id: objectIdStr,
  jobId: objectIdStr,
  name: z.string().min(1),
  order: z.number().int().default(0),
  templateName: z.string().optional(),
  dateStart: dateStr,
  dateDelivery: dateStr,
  routing: z.array(routingStep),
});
export type Batch = z.infer<typeof batch>;

export const routingStepWrite = z.object({
  stationKey,
  hoursWork: z.number().min(0),
  bufferDays: z.number().int().min(0).optional(),
  notBeforeDate: dateStr.optional().nullable(),
  maxHoursPerDay: z.number().positive().optional(),
  dependsOn: z.array(stationKey).optional(),
});
export type RoutingStepWrite = z.infer<typeof routingStepWrite>;

export const jobCreate = z.object({
  name: z.string().min(1),
  customer: z.string().optional(),
  priority: z.number().int().optional(),
  color: hexColor.optional(),
  status: jobStatus.optional(),
  /** Seeds the job's single default batch. */
  templateName: z.string().optional(),
  dateStart: dateStr,
  dateDelivery: dateStr,
  routing: z.array(routingStepWrite).optional(),
});
export type JobCreate = z.infer<typeof jobCreate>;

export const jobPatch = z.object({
  name: z.string().min(1).optional(),
  customer: z.string().optional().nullable(),
  priority: z.number().int().optional(),
  color: hexColor.optional(),
  status: jobStatus.optional(),
  /** `true` puts the job in the archive, `false` restores it. Reversible, and it
   * touches nothing else — status, plan and bookings all stay as they are. */
  archived: z.boolean().optional(),
});
export type JobPatch = z.infer<typeof jobPatch>;

/** What `POST /jobs/archive-finished` did: how many done/cancelled jobs it put away. */
export const archiveFinishedResult = z.object({ archived: z.number().int() });
export type ArchiveFinishedResult = z.infer<typeof archiveFinishedResult>;

export const batchCreate = z.object({
  name: z.string().min(1),
  order: z.number().int().optional(),
  templateName: z.string().optional(),
  dateStart: dateStr,
  dateDelivery: dateStr,
  routing: z.array(routingStepWrite).optional(),
});
export type BatchCreate = z.infer<typeof batchCreate>;

export const batchPatch = z.object({
  name: z.string().min(1).optional(),
  order: z.number().int().optional(),
  dateStart: dateStr.optional(),
  dateDelivery: dateStr.optional(),
  routing: z.array(routingStepWrite).optional(),
});
export type BatchPatch = z.infer<typeof batchPatch>;

export const stepPatch = z.object({
  status: stepStatus,
});
export type StepPatch = z.infer<typeof stepPatch>;

/**
 * One drag on the board. The gesture is fully described by the chip that was picked up
 * and the column it was dropped on: everything from the grabbed chip onward moves,
 * everything before it stays.
 */
export const planMove = z.object({
  stationKey,
  /** The day of the chip that was grabbed — the point the move starts from. */
  fromDate: dateStr,
  /**
   * Which booking on that day was grabbed. A station can hold two bookings of the same
   * batch on one date — a pinned one and the hours a later re-plan added beside it —
   * and `fromDate` alone cannot tell them apart, so the board says which chip it was.
   * Optional because the date is still what the *move* means; the id only disambiguates.
   */
  bookingId: objectIdStr.optional(),
  /** The column it was dropped on. */
  toDate: dateStr,
});
export type PlanMove = z.infer<typeof planMove>;

/** Promotes a hand-move into a real constraint, so it survives "Clear hand-moves". */
export const planFix = z.object({ stationKey });
export type PlanFix = z.infer<typeof planFix>;
